/**
 * The new-experiment flow's unsaved choices, kept in sessionStorage so a reload,
 * or a trip to upload a dataset, comes back to the same step with the same
 * picks. One per starting point (blank, an agent, a saved draft, a copied
 * experiment), per tab;
 * dropped once the experiment is saved or the person starts over.
 *
 * Storage can be missing or throw (private window, blocked site data): then
 * nothing is kept and the flow still works.
 */

import { useEffect, useRef } from "react";
import { getProjectSlug } from "@/lib/api";
import type { TargetDraft } from "@/features/connectors/types";
import type { PeriodChoice } from "@/features/traces/period-picker";
import type { InputMapping } from "../types";
import type { KeptVersion } from "./pulls/hooks";

/** Who answers each item: the agent, run now, or the outputs already in the dataset. */
export type Runner = "agent" | "outputs";

export interface FormDraft {
  name: string;
  description: string;
  datasetId: string | null;
  runner: Runner | null;
  /** Evaluator ids, or null while the default selection applies. */
  picked: string[] | null;
  repetitions: number;
  costLimit: string;
  runSettings: Record<string, string>;
  credentialRefs: Record<string, string>;
  inputMapping: InputMapping | null;
}

/** What was chosen; a field left out falls back to the flow's own default. */
export interface WizardDraft {
  step: number;
  connectionId?: string | null;
  target?: TargetDraft;
  form: Partial<FormDraft>;
  /** A live dataset's version pick, "Pull next" and period. */
  live?: KeptVersion & { period: PeriodChoice };
}

const prefix = () => `workbench:new-experiment:${getProjectSlug() ?? "root"}:`;

export function draftKey(start: {
  agentId?: string | undefined;
  draftId?: string | undefined;
  copyOf?: string | undefined;
}): string {
  if (start.draftId) return `${prefix()}draft:${start.draftId}`;
  if (start.copyOf) return `${prefix()}copy:${start.copyOf}`;
  return start.agentId ? `${prefix()}agent:${start.agentId}` : `${prefix()}new`;
}

export function loadDraft(key: string): WizardDraft | null {
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as WizardDraft) : null;
    return parsed && typeof parsed === "object" && parsed.form ? parsed : null;
  } catch {
    return null;
  }
}

export function clearDraft(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing was kept, so there is nothing to drop.
  }
}

/** Write the draft whenever it changes. A `fresh` flow (nothing restored) waits
 *  for its first change, so an untouched flow keeps nothing. */
export function useKeepDraft(key: string, draft: WizardDraft, fresh: boolean) {
  const json = JSON.stringify(draft);
  const first = useRef(fresh ? json : null);
  useEffect(() => {
    if (json === first.current) return;
    try {
      sessionStorage.setItem(key, json);
    } catch {
      // Storage full or blocked: the flow carries on without it.
    }
  }, [key, json]);
}

/** A saved draft with `datasetId` picked (back from creating it), opened on the dataset step. */
/** Back from creating an evaluator: the evaluators step, with it ticked. Untouched
 *  ticks (`picked` null) are the defaults, which take a new free check anyway. */
export function withEvaluator(saved: WizardDraft | null, evaluatorId: string | null): WizardDraft | null {
  if (!evaluatorId) return saved;
  const base = saved ?? { step: 0, form: {} };
  const picked = base.form.picked;
  if (!picked) return { ...base, step: 3 };
  return { ...base, step: 3, form: { ...base.form, picked: [...new Set([...picked, evaluatorId])] } };
}

export function withDataset(saved: WizardDraft | null, datasetId: string | null): WizardDraft | null {
  if (!datasetId) return saved;
  const base = saved ?? { step: 0, form: {} };
  return { ...base, step: 1, form: { ...base.form, datasetId } };
}
